import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import AdvancedStats from "@/components/AdvancedStats";
import ServiceProductCosts from "@/components/ServiceProductCosts";
import { BarChart3, Loader2 } from "lucide-react";

const eur = (n) => new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(n || 0);
const SRC_LBL = { non_renseigne: "Non renseigné", google: "Google", google_maps: "Google Maps", facebook: "Facebook", instagram: "Instagram", tiktok: "TikTok", bouche_a_oreille: "Bouche-à-oreille", ancien_client: "Ancien client", google_ads: "Google Ads", meta_ads: "Publicité Meta", site_internet: "Site internet", passage: "Passage devant le local", partenaire: "Partenaire", autre: "Autre" };

function Kpi({ label, value, accent }) {
  return <div className="bg-white border border-slate-200 rounded-xl p-4"><div className="text-xs text-slate-400">{label}</div><div className={`text-xl font-bold mt-1 ${accent || "text-slate-900"}`}>{value}</div></div>;
}

export default function Analytics() {
  const today = new Date().toISOString().slice(0, 10);
  const [start, setStart] = useState(today.slice(0, 8) + "01");
  const [end, setEnd] = useState(today);
  const [data, setData] = useState(null);
  const [prof, setProf] = useState([]);
  const [groupBy, setGroupBy] = useState("service");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [o, p] = await Promise.all([
        api.get("/analytics/overview", { params: { start, end } }),
        api.get("/analytics/profitability", { params: { start, end, group_by: groupBy } }),
      ]);
      setData(o.data); setProf(p.data);
    } catch (e) { /* silent */ } finally { setLoading(false); }
  }, [start, end, groupBy]);
  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <PageHeader title="Analytics métier" subtitle="CA, marge, rentabilité, sources et conversion" icon={BarChart3} />
      <div className="flex flex-wrap gap-2 mb-5 items-end" data-testid="analytics-filters">
        <div><label className="text-xs text-slate-400 block mb-1">Du</label><input type="date" value={start} onChange={(e) => setStart(e.target.value)} data-testid="analytics-start" className="h-9 rounded-lg border border-slate-200 px-2 text-sm" /></div>
        <div><label className="text-xs text-slate-400 block mb-1">Au</label><input type="date" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="analytics-end" className="h-9 rounded-lg border border-slate-200 px-2 text-sm" /></div>
      </div>

      {loading || !data ? <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-emerald-600" /></div> : (
        <div className="space-y-6" data-testid="analytics-content">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Kpi label="Chiffre d'affaires" value={eur(data.ca)} />
            <Kpi label="Encaissé" value={eur(data.encaisse)} accent="text-emerald-600" />
            <Kpi label="Panier moyen" value={eur(data.panier_moyen)} />
            <Kpi label="Prestations facturées" value={data.nb_prestations} />
            <Kpi label="Marge estimée" value={eur(data.margin_total)} accent={data.margin_total >= 0 ? "text-emerald-600" : "text-red-600"} />
            <Kpi label="Impayés" value={`${data.unpaid_count} · ${eur(data.unpaid_amount)}`} accent="text-red-600" />
            <Kpi label="Devis → Facture" value={data.transformation_rate != null ? `${data.transformation_rate}%` : "—"} />
            <Kpi label="Nouveaux / Récurrents" value={`${data.new_clients} / ${data.recurring_clients}`} />
            <Kpi label="Frais déplacement" value={`${eur(data.travel_fees)} · ${data.km} km`} />
            <Kpi label="Annulés / Absents" value={`${data.cancelled} / ${data.absent}`} />
          </div>

          <AdvancedStats start={start} end={end} />
          <ServiceProductCosts />

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white border border-slate-200 rounded-xl p-5">
              <h3 className="font-heading text-base font-semibold text-slate-900 mb-3">Sources d'acquisition</h3>
              <div className="space-y-1.5" data-testid="analytics-sources">
                {data.sources.map((s) => (
                  <div key={s.source} className="flex items-center justify-between text-sm py-1 border-b border-slate-100 last:border-0">
                    <span className="text-slate-700">{SRC_LBL[s.source] || s.source}</span>
                    <span className="text-slate-500">{s.count} client(s) · {eur(s.ca)} · panier {eur(s.panier)}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-white border border-slate-200 rounded-xl p-5">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-heading text-base font-semibold text-slate-900">Rentabilité</h3>
                <select value={groupBy} onChange={(e) => setGroupBy(e.target.value)} data-testid="analytics-groupby" className="h-8 rounded-lg border border-slate-200 text-sm px-2">
                  <option value="service">Par prestation</option><option value="category">Par catégorie</option><option value="client">Par client</option><option value="vehicle">Par véhicule</option>
                </select>
              </div>
              <div className="space-y-1.5" data-testid="analytics-profitability">
                {prof.length > 0 && <div className="grid grid-cols-2 gap-2 mb-2 text-xs">
                  <div className="rounded-lg bg-emerald-50 p-2" data-testid="prof-best"><div className="text-slate-500">Plus rentable (marge/h)</div><b className="text-emerald-700 break-words">{[...prof].filter((g) => g.hourly_profit != null).sort((a, b) => b.hourly_profit - a.hourly_profit)[0]?.key || "—"}</b></div>
                  <div className="rounded-lg bg-red-50 p-2" data-testid="prof-worst"><div className="text-slate-500">Moins rentable (marge/h)</div><b className="text-red-700 break-words">{[...prof].filter((g) => g.hourly_profit != null).sort((a, b) => a.hourly_profit - b.hourly_profit)[0]?.key || "—"}</b></div>
                </div>}
                {prof.length ? prof.slice(0, 12).map((g) => (
                  <div key={g.key} className="text-sm py-1.5 border-b border-slate-100 last:border-0" data-testid="prof-row">
                    <div className="flex items-center justify-between gap-2"><span className="text-slate-700 truncate">{g.key}</span>
                    <span className={`shrink-0 ${g.margin >= 0 ? "text-emerald-600" : "text-red-600"}`}>{eur(g.margin)}{g.margin_rate != null ? ` (${g.margin_rate}%)` : ""}</span></div>
                    <div className="text-[11px] text-slate-400">{g.count} RDV · CA {eur(g.revenue)} · {g.hourly_profit != null ? `${eur(g.hourly_profit)}/h` : "—"} · temps moy. {g.avg_hours ?? "—"} h · consommables moy. {eur(g.avg_product_cost)}{g.time_gap_hours != null ? ` · écart réel/prévu ${g.time_gap_hours > 0 ? "+" : ""}${g.time_gap_hours} h` : ""}</div>
                  </div>
                )) : <p className="text-sm text-slate-500">Aucune donnée sur la période.</p>}
                <p className="text-[11px] text-slate-400 pt-1">Marge estimée = CA − produits − main-d'œuvre (coût horaire défini dans Paramètres) − déplacements − autres frais directs. Ce n'est pas un résultat comptable.</p>
              </div>
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl p-5">
            <h3 className="font-heading text-base font-semibold text-slate-900 mb-3">Prestations les plus facturées</h3>
            <div className="space-y-1.5">
              {data.top_services.map((t) => <div key={t.name} className="flex justify-between text-sm py-1 border-b border-slate-100 last:border-0"><span className="text-slate-700 truncate">{t.name}</span><span className="text-slate-500">{eur(t.ca)}</span></div>)}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
